import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MultiplayerLobby.css';

const KBCMultiplexChallenge = ({ 
  userRole, 
  userName, 
  selectedQuestions,
  onStartGame, 
  onBack 
}) => {
  const [onlineTeachers, setOnlineTeachers] = useState([]);
  const [challengeSent, setChallengeSent] = useState(false);
  const [challengedPlayer, setChallengedPlayer] = useState('');
  const [loading, setLoading] = useState(false);
  const [requestId, setRequestId] = useState('');

  useEffect(() => {
    fetchOnlineTeachers();
    const interval = setInterval(fetchOnlineTeachers, 3000);
    return () => clearInterval(interval);
  }, []);

  const fetchOnlineTeachers = async () => {
    try {
      const response = await axios.get('/api/games/online-players');
      const teachers = response.data.filter(player => 
        ['teacher', 'admin', 'admin1', 'teacher_admin_level1'].includes(player.role) &&
        player.name !== userName
      );
      setOnlineTeachers(teachers);
    } catch (error) {
      console.error('Error fetching online teachers:', error);
    }
  };

  const sendChallenge = async (targetPlayer) => {
    setLoading(true);
    try {
      const response = await axios.post('/api/kbc-challenges/send', {
        fromPlayer: userName,
        fromRole: userRole,
        toPlayer: targetPlayer,
        questions: selectedQuestions,
        gameConfig: {
          subject: selectedQuestions[0]?.subject || 'Mixed',
          class: selectedQuestions[0]?.class || 'All'
        }
      });

      if (response.data.success) {
        setChallengeSent(true);
        setChallengedPlayer(targetPlayer);
        setRequestId(response.data.requestId);
        
        // Check for acceptance every 1 second
        const checkInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(`/api/kbc-challenges/status/${response.data.requestId}`);
            if (statusResponse.data.accepted && statusResponse.data.gameSession) {
              clearInterval(checkInterval);
              console.log('Challenge accepted! Starting game with session:', statusResponse.data.gameSession);
              onStartGame({
                mode: 'kbc-host-contestant',
                questions: selectedQuestions,
                gameSession: statusResponse.data.gameSession
              });
            } else if (statusResponse.data.rejected) {
              clearInterval(checkInterval);
              setChallengeSent(false);
              setChallengedPlayer('');
              alert('Challenge was rejected');
            }
          } catch (error) {
            console.error('Error checking challenge status:', error);
          }
        }, 1000);

        // Stop checking after 1 minute and cleanup
        setTimeout(() => {
          clearInterval(checkInterval);
          setChallengeSent(false);
          setChallengedPlayer('');
          // Clean up the challenge on server
          axios.delete(`/api/kbc-challenges/${response.data.requestId}`).catch(console.error);
        }, 60000);
      }
    } catch (error) {
      console.error('Error sending challenge:', error);
      alert('Failed to send challenge');
    } finally {
      setLoading(false);
    }
  };

  if (challengeSent) {
    return (
      <div className="multiplayer-lobby">
        <div className="lobby-container">
          <h2>🎯 Challenge Sent!</h2>
          
          <div style={{ textAlign: 'center', padding: '30px' }}>
            <div style={{ 
              background: '#f0f9ff', 
              border: '2px solid #0ea5e9', 
              borderRadius: '12px', 
              padding: '20px', 
              marginBottom: '20px' 
            }}>
              <h3 style={{ color: '#0369a1', marginBottom: '15px' }}>⏳ Waiting for Response</h3>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>
                Challenge sent to: <strong>{challengedPlayer}</strong>
              </div>
              <div style={{ fontSize: '14px', color: '#0369a1' }}>
                {selectedQuestions.length} questions • {selectedQuestions[0]?.subject || 'Mixed'} • No time limit
              </div>
            </div>

            <div className="loading-spinner" style={{ margin: '20px auto' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f4f6', 
                borderTop: '4px solid #8b5cf6', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite' 
              }}></div>
            </div>

            <div style={{ fontSize: '14px', color: '#666', marginTop: '20px' }}>
              Waiting for {challengedPlayer} to accept the challenge...
            </div>
          </div>

          <div className="action-buttons">
            <button 
              onClick={() => { 
                setChallengeSent(false); 
                setChallengedPlayer('');
                // Clean up the challenge on server
                axios.delete(`/api/kbc-challenges/${requestId}`).catch(console.error);
              }} 
              className="exit-btn"
            >
              Cancel Challenge
            </button>
            <button onClick={onBack} className="exit-btn">
              Back to Questions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="multiplayer-lobby">
      <div className="lobby-container">
        <h2>🎯 Send KBC Challenge</h2>
        
        <div style={{ 
          background: '#f0fdf4', 
          border: '2px solid #16a34a', 
          borderRadius: '8px', 
          padding: '15px', 
          marginBottom: '20px',
          textAlign: 'center'
        }}>
          <div style={{ color: '#15803d', fontWeight: 'bold', marginBottom: '5px' }}>
            ✅ {selectedQuestions.length} Questions Ready
          </div>
          <div style={{ fontSize: '12px', color: '#166534' }}>
            {selectedQuestions[0]?.subject || 'Mixed'} • 
            {selectedQuestions[0]?.class || 'All'} • 
            No Time Limit
          </div>
        </div>

        <div className="lobby-options">
          <h3>Online Teachers ({onlineTeachers.length})</h3>
          
          {onlineTeachers.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '30px', 
              color: '#666',
              background: '#f9fafb',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>👥</div>
              <div>No other teachers online</div>
              <div style={{ fontSize: '12px', marginTop: '5px' }}>
                Ask another teacher to login and go to Games section
              </div>
            </div>
          ) : (
            <div className="teachers-list">
              {onlineTeachers.map(teacher => (
                <div key={teacher.name} className="teacher-card" style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '10px',
                  background: 'white',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                      👨‍🏫 {teacher.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {teacher.role} • Class {teacher.class || 'All'}
                    </div>
                  </div>
                  <button
                    onClick={() => sendChallenge(teacher.name)}
                    disabled={loading}
                    style={{
                      background: '#8b5cf6',
                      color: 'white',
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                  >
                    {loading ? 'Sending...' : 'Challenge'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button onClick={onBack} className="exit-btn">
            Back to Questions
          </button>
        </div>
      </div>
    </div>
  );
};

export default KBCMultiplexChallenge;